import { useAuth } from "../../store/AuthContext/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { useEffect, useCallback, useMemo, useState, useRef } from "react";
import Sidebar from "../../components/Sidebar/Sidebar";
import AppHeader from "../../components/AppHeader/AppHeader";
import ApiManager from "../../api/ApiManager";
import Paywall from "../../components/Paywall/Paywall";
import { getCountdownFrom72Hours } from "../../utils/timeUtils";

export default function BaseAppLayout({ children, headerText }) {
  const { isAuthenticated, isInitialized, login, userCreatedAt, isSubbed } = useAuth();
  const [timer, setTimer] = useState(getCountdownFrom72Hours(userCreatedAt));

  if (!isSubbed && timer === false) {
    console.log("No sub and timer expired");

    //TODO: Redirect to upgrade page
  }

  const navigate = useNavigate();
  const location = useLocation();

  const api = useMemo(() => new ApiManager(), []);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  const { getCookie } = useAuth();

  // Funzione per aprire il Paywall
  const handleUpgradeClick = () => {
    setIsUploadModalOpen(true);
  };

  const exchangeCodeForToken = useCallback(
    async (code) => {
      try {
        const body = new URLSearchParams({
          grant_type: "authorization_code",
          client_id: "bo24stke7qeq3l76saqttte3j",
          client_secret: "jf4njijdjir4hoj00u5v7lai9a191o670a4t9p3vf7j35kfcli0",
          code: code,
          redirect_uri: "http://localhost:3000/client/library",
        });

        const response = await fetch(
          `https://${process.env.REACT_APP_COGNITO_DOMAIN}/oauth2/token`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
            },
            body: body,
          }
        );

        const data = await response.json();
        console.log("Token ricevuti:", data);

        if (data.access_token) {
          // Decodifica il token per ottenere il valore di 'sub'
          const payload = JSON.parse(atob(data.access_token.split(".")[1]));
          const userSub = payload.sub;

          if (!userSub) {
            console.error("Errore: 'sub' non trovato nel token.");
            return;
          }

          login({ data });

          api
            .get(`/projects/fetch?user_sub=${userSub}`, { headers: { Authorization: `Bearer ${data.id_token}` } })
            .then((response) => {
              try {
                const data = response.data.body
                  ? JSON.parse(response.data.body)
                  : null;
                if (
                  !data ||
                  !data.projects ||
                  !Array.isArray(data.projects) ||
                  data.projects.length === 0
                ) {
                  console.error(
                    "Errore: Nessun progetto trovato o dati non validi.",
                    data
                  );
                  return;
                }
                const project = data.projects[0]; // Prende il primo progetto

                if (!project.pk || typeof project.pk !== "string") {
                  console.error("Errore: Il campo 'pk' non è valido.", project);
                  return;
                }

                // Rimuove 'project#' in modo sicuro
                const projectId = project.pk.startsWith("project#")
                  ? project.pk.slice(8)
                  : project.pk;

                if (!projectId || projectId.trim() === "") {
                  console.error(
                    "Errore: project_id è vuoto o non valido dopo il parsing."
                  );
                  return;
                }

                // Imposta il cookie solo se il projectId è valido
                document.cookie = `project_id=${projectId}; path=/`;

                // Reindirizza alla libreria
                navigate("/client/library", { replace: true });
              } catch (error) {
                console.error("Errore nel parsing della risposta API:", error);
              }
            })
            .catch((error) => {
              console.error("Errore nella chiamata API:", error);
            });
        } else {
          console.error("Errore nel recupero del token:", data);
        }
      } catch (error) {
        console.error("Errore nella richiesta dei token:", error);
      }
    },
    [login, navigate]
  );

  const handleOnboarding = useCallback(async () => {
    const onboardingData =
      JSON.parse(localStorage.getItem("onboardingAnswers")) || {};
    try {
      if (Object.keys(onboardingData).length === 0) {
        // console.log(
        //   "Nessun dato di onboarding trovato nel localStorage. Skipping API call."
        // );
        return; // Se l'item non esiste, salta l'API call
      }

      const userSub = getCookie("auth_token");
      const idToken = getCookie("id_token");

      if (!userSub) {
        console.error("Errore: user_sub non trovato nel cookie.");
        return;
      }

      const onboardingResponse = await api.post(
        "/onboarding",
        {
          user_sub: userSub,
          discoveryChannel: onboardingData.discoveryChannel,
          dueRemainingTime: onboardingData.dueRemainingTime,
          extraInfo: onboardingData.extraInfo,
          extraInfoText: onboardingData.extraInfoText,
          studyField: onboardingData.studyField,
          thesisPrompt: onboardingData.thesisPrompt,
          thesisType: onboardingData.thesisType,
        },
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        }
      );


      console.log("Onboarding Response:", onboardingResponse);
      localStorage.removeItem("onboardingAnswers");
    } catch (error) {
      console.error("Errore durante la chiamata onboarding:", error);
    }
  }, [api]);

  useEffect(() => {
    if (!isInitialized) return;

    const params = new URLSearchParams(location.search);
    const code = params.get("code");

    if (isAuthenticated) {
      // Chiama l'onboarding solo se c'è l'item nel localStorage
      handleOnboarding();
      return;
    }

    if (code) {
      exchangeCodeForToken(code);
    } else if (!isAuthenticated) {
      console.log("NO CODE FOUND");
      navigate("/login");
    }
  }, [
    isAuthenticated,
    isInitialized,
    location,
    navigate,
    handleOnboarding,
    exchangeCodeForToken,
  ]);

  const sidebarRef = useRef(null);
  const [sidebarHeight, setSidebarHeight] = useState(0);

  useEffect(() => {
    const updateHeight = () => {
      if (sidebarRef.current) {
        setSidebarHeight(sidebarRef.current.offsetHeight);
      }
    };

    updateHeight();
    window.addEventListener("resize", updateHeight);

    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  return (
    <div className="w-full h-screen flex flex-row bg-gray-50 overflow-y-hidden overflow-x-hidden">
      <Sidebar ref={sidebarRef} onUpgradeClick={handleUpgradeClick} />
      <div className="md:w-5/6 w-full h-screen flex flex-col bg-gray-50">
        <AppHeader text={headerText} onUpgradeClick={handleUpgradeClick} />
        <div
          className="w-full h-full grow-1 bg-gray-50 overflow-x-hidden overflow-y-auto relative"
          style={{
            paddingBottom: window.innerWidth < 768 ? `${sidebarHeight}px` : "",
          }}
        >
          {children}
        </div>
      </div>
      <Paywall
        open={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
      />
    </div>
  );
}
